/* eslint-disable security/detect-non-literal-fs-filename */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  ActivitiesFeedList,
  FeedService,
} from 'src/app/core/services/feed/feed.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { FeedItem } from 'src/app/shared/models/feed-item/feed-item';
import { CouponCardService } from '../../../../core/services/coupon-card/coupon-card.service';
import { FilterOptions } from 'src/app/shared/models/filters/filter-options';
import { FormControl, Validators } from '@angular/forms';
import { FilterType } from 'src/app/shared/models/constants/filter-type';
import { UserService } from 'src/app/core/api/user/user.service';
import {
  generateSeriesChips,
  MultiSelectChip,
} from '../filter-container/filter-container.component';
import { ProductService } from 'src/app/core/api/product/product.service';

@Component({
  selector: 'app-feed',
  templateUrl: './feed.component.html',
  styleUrls: ['./feed.component.scss'],
})
/**
 * Displays the feed
 */
export class FeedComponent implements OnInit, OnDestroy {
  private pageSize = 25;
  private page = 0;
  feedItems?: FeedItem[] = [];
  countries: any[] = [];
  monthsStrings: { [key0: number]: string } = {};
  months: { [key0: number]: { [key0: number]: FeedItem } } = {};
  seriesChips: MultiSelectChip[] = [];
  statusChips = ChipContent.StatusCoupon();
  chipContents: ChipContent[] = [];
  pullingFeed = false;
  search = new FormControl('', [Validators.minLength(3)]);
  currentSearch?: string | null;
  filters: FilterOptions = {
    vip: false,
    dateRange: { startDate: undefined, endDate: undefined },
    coupons: false,
    status: [],
    series: [],
  };
  labels = {
    activitiesFeed: 'activities-feed',
    notFound: 'activities-empty',
    notFoundDescription: 'feed-not-listed-description',
  };

  private subsink = new Subscription();

  /**
   * @constructor
   */
  constructor(
    private feedService: FeedService,
    private productService: ProductService,
    private userService: UserService,
    private translationService: TranslationService,
    private readonly couponCardService: CouponCardService
  ) {
    const dateChip = new ChipContent(
      undefined,
      'dateRange',
      FilterType.DATE_RANGE,
      Boolean(
        this.feedService.filters.startDate || this.feedService.filters.endDate
      )
    );
    const vipChip = ChipContent.Vip();
    const couponsChip = ChipContent.Coupons();
    this.chipContents.push(dateChip, vipChip, couponsChip);
  }

  /**
   * on init
   */
  ngOnInit(): void {
    this.productService.getAllFamilyProduct().subscribe({
      next: (productSeriesList) => {
        this.seriesChips = generateSeriesChips(productSeriesList);
      },
    });

    this.userService.getCountries().subscribe((countries) => {
      countries.sort((a, b) => a.name.localeCompare(b.name));
      this.countries = countries;

      this.filters.countries = this.countries?.map((data) => data.id) ?? [];
      this.translationService.translateObject(this.labels);
      this.setFilters(this.filters);
      this.pullFeed();
    });
  }

  /**
   * Updates Filters
   * @param {AdminFilters} filters
   */
  updateFilter(filters: any) {
    filters.countries = this.filters.countries;
    this.filters = filters;

    this.setFilters(this.filters);
    this.pullFeed();
  }

  /**
   * Update the country filter values and calls the method to retrieve activites feed
   * @param {string[]} selectedValues
   */
  onCountryFilterChanged(selectedValues: string[]) {
    // If countries list is not loaded yet, the feed shall not be pulled
    if (this.countries.length == 0) {
      return;
    }
    this.filters.countries = selectedValues;
    this.updateFilter(this.filters);
  }

  /**
   * cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
    this.couponCardService.couponStatuses = [];
  }

  /**
   * Group and sort cards by month and year
   * @param {ActivitiesFeedList} feed
   */
  private groupByMonth(feed: ActivitiesFeedList) {
    feed.forEach((item) => {
      const candidateDate = item.displayDate;
      const date = new Date(candidateDate);
      const month = date.getMonth();
      const year = date.getFullYear();
      this.months[year] = this.months[year] || {};
      if (this.months[year][month] == null) {
        this.months[year][month] = new FeedItem(month, year, []);
        this.translateMonth(month).then((translation) => {
          this.monthsStrings[month] = translation;
        });
        this.feedItems!.push(this.months[year][month]);
      }
      this.months[year][month].items.push(item);
    });

    this.feedItems?.sort((a, b) => b.month - a.month);
    this.feedItems?.sort((a, b) => b.year - a.year);
  }

  /**
   * Add more items to feed when scrolling
   * @param {boolean} search
   */
  pullFeed() {
    if (this.pullingFeed) {
      return;
    }
    this.pullingFeed = true;
    this.feedService.fetchCoupons(this.page, this.pageSize).then((feed) => {
      if (feed.length) {
        this.page++;
        this.groupByMonth(feed);
      }
      this.pullingFeed = false;
    });
  }

  /**
   * @param {number} month
   * @return {Promise<string>}
   */
  async translateMonth(month: number): Promise<string> {
    return this.translationService.translateString(`month-${month}`);
  }

  /**
   * @param {number} month
   * @return {string}
   */
  getMonthString(month: number): string {
    if (this.monthsStrings[month] == null) {
      this.translateMonth(month).then((translation) => {
        this.monthsStrings[month] = translation;
      });
      return '';
    }
    return this.monthsStrings[month];
  }

  /**
   * Generate filter params and emit it
   * @param {FilterOptions} filters
   */
  setFilters(filters: FilterOptions) {
    this.feedService.filters.vip = filters.vip ?? false;
    this.feedService.filters.countries = filters.countries!;
    this.feedService.filters.status = filters.status!;
    this.feedService.filters.series = filters.series!;

    if (this.search.getRawValue()!.length >= 3) {
      this.feedService.filters.search = this.search.getRawValue()!;
    } else {
      this.feedService.filters.search = '';
    }

    if (filters.dateRange) {
      Object.assign(this.feedService.filters, filters.dateRange);
    }
    this.page = 0;
    this.months = [];
    this.feedItems = [];
  }

  /**
   * Update coupon
   * @param {any} coupon
   */
  updateCoupon(coupon: any) {
    if (!this.feedItems || !coupon.originalDate) {
      return;
    }
    const originalMonth = new Date(coupon.originalDate).getMonth();
    const feedItemIndex = this.feedItems.findIndex(
      (feedItemEl) => feedItemEl.month === originalMonth
    );
    const feedItem = this.feedItems[feedItemIndex];
    const indexItem = feedItem.items.findIndex(
      (item) => item.couponId === coupon.couponId
    );
    feedItem.items.splice(indexItem, 1);
    this.feedItems[feedItemIndex] = feedItem;
    this.feedItems[0].items.unshift(coupon);
  }
}
